/**
 * Random number + UUID generator — pure logic.
 *
 * The randomness is injected (`randomBelow(maxExclusive)` for integers, a byte
 * array for UUIDs), so everything is deterministic and unit-testable; the
 * component supplies CSPRNG-backed implementations (`crypto.getRandomValues`).
 */

/** Integer in [min, max] inclusive, using `randomBelow(n) → [0, n)`. */
export function randomIntInRange(
  min: number,
  max: number,
  randomBelow: (maxExclusive: number) => number,
): number | null {
  if (!Number.isInteger(min) || !Number.isInteger(max) || min > max)
    return null;
  return min + randomBelow(max - min + 1);
}

/**
 * `count` random integers in [min, max]. With `allowDuplicates` false the result
 * is unique and capped at the range size.
 */
export function generateIntegers(
  min: number,
  max: number,
  count: number,
  allowDuplicates: boolean,
  randomBelow: (maxExclusive: number) => number,
): number[] {
  if (!Number.isInteger(min) || !Number.isInteger(max) || min > max) return [];
  if (!Number.isInteger(count) || count <= 0) return [];
  const rangeSize = max - min + 1;

  if (allowDuplicates) {
    return Array.from({ length: count }, () => min + randomBelow(rangeSize));
  }

  const n = Math.min(count, rangeSize);
  if (rangeSize <= 100_000) {
    // Fisher-Yates over the full pool, then take the first n.
    const pool = Array.from({ length: rangeSize }, (_, i) => min + i);
    for (let i = rangeSize - 1; i > 0; i--) {
      const j = randomBelow(i + 1);
      const tmp = pool[i];
      pool[i] = pool[j];
      pool[j] = tmp;
    }
    return pool.slice(0, n);
  }
  // Huge range: rejection sampling into a set (n is small).
  const set = new Set<number>();
  while (set.size < n) set.add(min + randomBelow(rangeSize));
  return [...set];
}

/** Format 16 bytes as a RFC-4122 version-4 UUID. */
export function uuidV4(bytes: ArrayLike<number>): string {
  const b: number[] = [];
  for (let i = 0; i < 16; i++) b.push((bytes[i] ?? 0) & 0xff);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx
  const hex = b.map((x) => x.toString(16).padStart(2, "0"));
  return (
    `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-` +
    `${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-` +
    `${hex.slice(10, 16).join("")}`
  );
}
